import React, { useCallback, useState } from 'react'
import L from 'leaflet'
import { useEffect } from 'react'
import { useMap } from 'react-leaflet'

interface MarkerData {
  id: string
  latlng: L.LatLngLiteral
  markerRef: L.Marker
  color: string
  distanceToShop: number
  duration: number
}

const OSRM_URL = 'http://router.project-osrm.org'  // Публичный демо-сервер (используйте с осторожностью)
const OSRM_PROFILE = 'driving'    

function getRandomColor () {
  var letters = '0123456789ABCDEF';
  var color = '#';
  for (var i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

export function createDistanceIcon({
  distance,
  duration,
  color = '#42b983' // можно передать любой цвет
}: { distance: number, duration: number, color?: string }) {
  // HTML разметка (inline-стили для упрощения)
  const iconHtml = `
    <div style="
      width: 34px;
      height: 34px;
      border-radius: 50%;
      background: ${color};
      color: #fff;
      font-size: 10px;
      font-weight: bold;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    ">
      <div>${distance.toFixed(0)} км</div>
      <div>${duration.toFixed(0)} м</div>
    </div>
  `

  // Создаём DivIcon
  return L.divIcon({
    html: iconHtml,
    className: '',        // убираем лишние классы Leaflet по умолчанию
    iconSize: [34, 34],   // размер иконки (совпадает с шириной/высотой блока)
    iconAnchor: [17, 17], // иконка якорится по центру (чтобы круг центрировался)
    popupAnchor: [0, -17] // если захотите сделать popup, смещение по вертикали
  })
}


export default function DistanceToShopInfo ({
  currentShopPoint
}: { currentShopPoint?: L.LatLngLiteral }) {
  const [ cursorDistance, setCursorDistance ] = useState<number>(0)
  const [ distanceMarkers, setDistanceMarkers ] = useState<MarkerData[]>([])
  const map = useMap()

  
  useEffect(() => {
    if (!map) return
    async function handleMapClick(e: L.LeafletMouseEvent) {
      if (!currentShopPoint) {
        setDistanceMarkers([])
        return
      }

      const latlng = e.latlng

      const color = getRandomColor()


      // Расчет расстояния
      const url = `${OSRM_URL}/route/v1/${OSRM_PROFILE}/${currentShopPoint.lng},${currentShopPoint.lat};${latlng.lng},${latlng.lat}?overview=false`
      const resp = await fetch(url)
      const data = await resp.json()
      const distanceOSRM = data.routes[0].distance / 1000
      const duration = data.routes[0].duration / 60 // секунды


      // Создаём сам маркер и добавляем его на карту
      const marker = L.marker(latlng, { 
        draggable: false,
        icon: createDistanceIcon({
          distance: distanceOSRM, 
          duration,
          color
        })
      }).addTo(map)

      // Генерируем ID (можно заменить на любую генерацию уникального ID)
      const markerId = crypto.randomUUID()

      const distance = e.latlng.distanceTo(currentShopPoint)

      // Запоминаем информацию о маркере в стейте
      setDistanceMarkers(prev => [
        ...prev,
        {
          id: markerId,
          latlng,
          distanceToShop: distanceOSRM,
          markerRef: marker,
          color,
          duration
        }
      ])
    }

    function handleMouseMove (e: L.LeafletMouseEvent) {
      // Если нет текущего магазина, расстояние = 0
      if (!currentShopPoint) {
        setCursorDistance(0)
        return
      }
      // Вычисляем расстояние (в метрах) от магазина до курсора
      const shopLatLng = L.latLng(currentShopPoint)
      const distance = shopLatLng.distanceTo(e.latlng)
      setCursorDistance(distance)
    }

    map.on('mousemove', handleMouseMove)
    map.on('click', handleMapClick)

    return () => {
      map.off('mousemove', handleMouseMove)
      map.off('click', handleMapClick)
    }
  }, [ currentShopPoint, map ])

  const removeMarkerById = useCallback((markerId: string) => {
    setDistanceMarkers(prev => {
      // Ищем нужный маркер
      const markerData = prev.find(m => m.id === markerId)
      if (markerData) {
        // Удаляем его с карты
        markerData.markerRef.remove()
      }
      // Убираем из массива
      return prev.filter(m => m.id !== markerId)
    })
  }, [])


  useEffect(() => {
    if (!map) return

    const control = new L.Control({ position: 'topleft' })

    control.onAdd = function () {
      const container = L.DomUtil.create(
        'div',
        'leaflet-bar leaflet-control leaflet-control-custom'
      )

      container.style.backgroundColor = 'white'
      container.style.padding = '4px 6px'
      container.style.cursor = 'auto'
      container.style.minWidth = '120px'
      container.style.font = '14px/1.2 sans-serif'

      L.DomEvent.disableClickPropagation(container)
      L.DomEvent.disableScrollPropagation(container)

      const markersListHtml = distanceMarkers
        .map(m => `
          <li style="margin-bottom: 4px; color: ${m.color}">
            ${m.distanceToShop.toFixed(3)} км., ${(m.duration).toFixed(2)} мин.
            <button
              class="remove-marker-btn"
              data-id="${m.id}"
              style="margin-left: 8px; cursor: pointer;"
            >
              Удалить
            </button>
          </li>
        `)
        .join('')

      container.innerHTML = `
        <div style="margin-bottom:4px;">
          <label style="display:block;margin-bottom:4px;">Расстояние до магазина: ${cursorDistance.toFixed(0)} м.</label>
          <div>
            <strong>Список маркеров:</strong>
            <ul style="margin: 6px 0 0 0; padding: 0 0 0 18px;">
              ${markersListHtml || '<li>Нет маркеров</li>'}
            </ul>
          </div>
          <span id="distanceToShop" style="display:block;margin-bottom:4px;"></span>
        </div>
      `

      return container
    }

    control.addTo(map)

    setTimeout(() => {
      const container = control.getContainer()
      if (!container) return
      
      const removeBtns = container.querySelectorAll('.remove-marker-btn')
  
      removeBtns.forEach(btn => {
        btn.addEventListener('click', (ev) => {
          ev.preventDefault()
          const target = ev.currentTarget as HTMLButtonElement
          const id = target.getAttribute('data-id')
          if (id) {
            removeMarkerById(id)
          }
        })
      })
    }, 0)

    return () => {
      map.removeControl(control)
    }
  }, [ map, cursorDistance, distanceMarkers ])

  return null
}